import { useParams, useNavigate, Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, ArrowLeft, Database } from 'lucide-react';
import { useAdminGameStates } from '../hooks';
import { GameStateCard } from '../components';

export function AdminUserStatesPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { states, loading, error } = useAdminGameStates(userId || '');

  if (loading) {
    return (
      <div className="p-6 max-w-4xl mx-auto space-y-4">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-4 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-4xl mx-auto space-y-4">
        <Link
          to="/a/arche_dfe_game_state/admin/users"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to User Lookup
        </Link>
        <Card className="border-destructive">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div>
        <Link
          to="/a/arche_dfe_game_state/admin/users"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to User Lookup
        </Link>
        <h1 className="text-2xl font-semibold">User Game States</h1>
        <p className="text-sm text-muted-foreground mt-1 font-mono">{userId}</p>
      </div>

      {states.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Database className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No game states found</h3>
            <p className="text-sm text-muted-foreground mt-1">
              This user has no stored game state data.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {states.map((state) => (
            <GameStateCard
              key={state.gameStateId}
              state={state}
              onClick={() =>
                navigate(
                  `/a/arche_dfe_game_state/admin/users/${encodeURIComponent(userId!)}/${encodeURIComponent(state.keyword)}`,
                )
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
